import React, { useState, useEffect, useRef } from 'react';
import { jobAgentVisible } from '../jobAgent/launch';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LEARNING_PATHS } from '../pages/LearningPath';
import { useElectron } from '../hooks/useElectron';
import LeftSidebar from './LeftSidebar';
import { SIDEBAR_SECTIONS } from '../config/sidebarConfig';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import LanguageSwitcher from './LanguageSwitcher';
import PinSetupModal from './PinSetupModal';
import PasskeySetupModal from './PasskeySetupModal';
import { passkeysSupported } from '../passkeyApi';
import Footer from './Footer';
import PasswordSetupModal from './PasswordSetupModal';
import SearchModal from './SearchModal';
import WelcomeTour from './WelcomeTour';
import api from '../api';
// Course and ebook notifications (ai_course_*, ai_ebook_*) through one set of helpers.
import {
  isAiNotification as isAiCourseNotification, anyAiNotificationIcon as aiNotificationIcon,
  anyAiNotificationText as aiNotificationText, anyAiNotificationPath as aiNotificationPath,
  visibleNotifications, NOTIFICATIONS_REFRESH,
} from '../ebookBuilder/notifications';


function SubscriptionBadge({ status }) {
  if (status === 'lifetime') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
      ♾ Lifetime
    </span>
  );
  if (status === 'active') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
      ✦ Premium
    </span>
  );
  if (status === 'expiring_soon') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/30">
      ⚠ Expiring
    </span>
  );
  if (status === 'expired') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
      ✕ Expired
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/30">
      Free
    </span>
  );
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function ProfilePanel({ onClose, hasPin, setHasPin, setShowPinModal, hasPassword, setHasPassword, setShowPwModal, hasPasskey, setShowPasskeyModal }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const ref = useRef(null);

  useEffect(() => {
    api.get('/user/profile')
      .then(({ data }) => setProfile(data))
      .catch(() => setProfile(null))
      .finally(() => setLoadingProfile(false));
  }, []);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const sub = profile?.subscription;
  const needsUpgrade = !sub || ['free', 'expired', 'expiring_soon'].includes(sub.status);
  const initials = user?.name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  return (
    <div
      ref={ref}
      className="absolute right-0 mt-2 w-72 rounded-2xl shadow-2xl shadow-black/40 z-50 animate-fade-in overflow-hidden"
      style={{ backgroundColor: 'var(--lx-surface)', border: '1px solid var(--lx-border)' }}
    >
      {/* User info */}
      <div className="p-4" style={{ borderBottom: '1px solid var(--lx-border)' }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">{initials}</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Subscription status */}
      <div className="p-4" style={{ borderBottom: '1px solid var(--lx-border)' }}>
        <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3">Membership</p>

        {loadingProfile ? (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 border border-indigo-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs text-gray-500">Loading…</span>
          </div>
        ) : !sub ? (
          <p className="text-xs text-gray-500">Unable to load subscription info.</p>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">Plan</span>
              <SubscriptionBadge status={sub.status} />
            </div>

            {/* Lifetime */}
            {sub.status === 'lifetime' && (
              <div className="mt-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-center">
                <p className="text-xl mb-1">🌟</p>
                <p className="text-xs font-semibold text-amber-300">Lifetime Member</p>
                <p className="text-xs text-amber-400/70 mt-0.5">Thank you for your lifetime support!</p>
              </div>
            )}

            {/* Active — show expiry */}
            {sub.status === 'active' && sub.expiry_at && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">Renews</span>
                <span className="text-xs text-gray-400">{formatDate(sub.expiry_at)}</span>
              </div>
            )}

            {/* Expiring soon */}
            {sub.status === 'expiring_soon' && (
              <div className="p-2.5 rounded-lg bg-orange-500/10 border border-orange-500/20">
                <p className="text-xs text-orange-300 font-medium">
                  ⚠ Expires in {sub.days_left} day{sub.days_left !== 1 ? 's' : ''} — {formatDate(sub.expiry_at)}
                </p>
              </div>
            )}

            {/* Expired */}
            {sub.status === 'expired' && (
              <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
                <p className="text-xs text-red-400 font-medium">
                  Expired on {formatDate(sub.expiry_at)}
                </p>
              </div>
            )}

            {/* Upgrade / Renew CTA */}
            {needsUpgrade && (
              <Link
                to="/membership"
                onClick={onClose}
                className="mt-2 flex items-center justify-center gap-1.5 w-full py-2 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-90 transition-opacity"
              >
                ✦ {sub.status === 'expired' || sub.status === 'expiring_soon' ? 'Renew Membership' : 'Upgrade to Premium'}
              </Link>
            )}

            {/* Upgrade to Lifetime — shown only for active yearly members */}
            {sub.status === 'active' && (
              <Link
                to="/membership"
                onClick={onClose}
                className="mt-2 flex items-center justify-center gap-1.5 w-full py-2 rounded-xl text-xs font-semibold transition-opacity hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #d97706, #f59e0b)', color: '#1c1400' }}
              >
                ♾ Upgrade to Lifetime
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-2">
        {/* Profile settings */}
        <button
          onClick={() => { navigate('/profile'); onClose(); }}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-gray-400 hover:text-white transition-colors"
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--lx-muted)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <span>👤</span>
          Profile Settings
        </button>

        {/* AI provider (BYOK) */}
        <button
          onClick={() => { navigate('/settings/ai-provider'); onClose(); }}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-gray-400 hover:text-white transition-colors"
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--lx-muted)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <span>🤖</span>
          {t('aiProvider.menuLabel')}
        </button>

        {/* Discord — paid members only */}
        {(sub?.status === 'active' || sub?.status === 'lifetime' || sub?.status === 'expiring_soon') && (
          <a
            href="https://discord.gg/QGnQcujs5z"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--lx-muted)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="flex-shrink-0">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            Discord Community ↗
          </a>
        )}

        {/* Password login toggle */}
        <button
          onClick={() => setShowPwModal(true)}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-gray-400 hover:text-white transition-colors"
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--lx-muted)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <span>🔑</span>
          {hasPassword ? 'Manage password login' : 'Set up password login'}
        </button>

        {/* Passkey login — hidden where the ceremony cannot succeed (desktop app,
            or a browser without WebAuthn) rather than failing on click. */}
        {passkeysSupported() && (
          <button
            onClick={() => setShowPasskeyModal(true)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-gray-400 hover:text-white transition-colors"
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--lx-muted)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span>🪪</span>
            {hasPasskey ? 'Manage passkey login' : 'Passkey login'}
          </button>
        )}

        {/* PIN login toggle */}
        <button
          onClick={() => setShowPinModal(true)}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-gray-400 hover:text-white transition-colors"
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--lx-muted)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <span>🔐</span>
          {hasPin ? 'Manage PIN login' : 'Set up PIN login'}
        </button>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-sm text-gray-400 hover:text-white transition-colors"
          style={{ ':hover': { backgroundColor: 'var(--lx-muted)' } }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--lx-muted)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <span className="flex items-center gap-2">
            <span>{theme === 'dark' ? '☀' : '🌙'}</span>
            {theme === 'dark' ? t('theme.lightMode') : t('theme.darkMode')}
          </span>
        </button>

        {/* Language switcher */}
        <LanguageSwitcher variant="menu" />

        {/* Invite Friends (Referral) */}
        <button
          onClick={() => { navigate('/referral'); onClose(); }}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--lx-muted)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <span>🔗</span>
          Invite Friends & Earn
        </button>

        {/* Sign out */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-gray-400 hover:text-red-400 transition-colors"
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--lx-muted)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Sign out
        </button>
      </div>

    </div>
  );
}

// ─── Notification Bell ────────────────────────────────────────────────────────
const NOTIF_ICONS = { like: '❤️', comment: '💬', reply: '↩️' };
const NOTIF_POLL_MS = 2 * 60_000;
const NOTIF_LABELS = {
  like:    (n) => `${n.actor_name} liked your post`,
  comment: (n) => `${n.actor_name} commented on your post`,
  reply:   (n) => `${n.actor_name} replied to your comment`,
};

function timeAgoShort(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/*
 * The bell. Every signed-in member gets it now: AI Course Builder
 * notifications (ai_course_*) share this table and go to everyone. The social
 * ones (like/comment/reply) are still shown only on a paid plan, as before.
 */
function NotificationBell({ signedIn, isPaid }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen]             = useState(false);
  const [notifications, setNotifs]  = useState([]);
  const [unread, setUnread]         = useState(0);
  const ref = useRef(null);

  // On load, every couple of minutes while the tab is in view, and at once
  // when a page asks (a course or lesson rewrite just finished).
  useEffect(() => {
    if (!signedIn) return undefined;
    let live = true;
    const fetchNotifs = () => api.get('/social/notifications')
      .then(({ data }) => {
        if (!live) return;
        const shown = visibleNotifications(data.notifications, isPaid);
        setNotifs(shown);
        setUnread(shown.filter((n) => !n.read).length);
      })
      .catch(() => {});
    fetchNotifs();
    const timer = setInterval(() => { if (!document.hidden) fetchNotifs(); }, NOTIF_POLL_MS);
    window.addEventListener(NOTIFICATIONS_REFRESH, fetchNotifs);
    return () => { live = false; clearInterval(timer); window.removeEventListener(NOTIFICATIONS_REFRESH, fetchNotifs); };
  }, [signedIn, isPaid]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    setOpen((v) => !v);
    if (!open && unread > 0) {
      setUnread(0);
      api.post('/social/notifications/read').catch(() => {});
      setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    }
  };

  const handleClick = (n) => {
    setOpen(false);
    if (isAiCourseNotification(n)) { navigate(aiNotificationPath(n)); return; }
    navigate(`/social/post/${n.post_id}`);
  };

  if (!signedIn) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleOpen}
        title="Notifications"
        className="relative w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-indigo-500 text-[9px] font-bold text-white flex items-center justify-center leading-none">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 rounded-2xl shadow-2xl shadow-black/40 z-50 overflow-hidden"
          style={{ backgroundColor: 'var(--lx-surface)', border: '1px solid var(--lx-border)' }}
        >
          <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--lx-border)' }}>
            <p className="text-sm font-semibold text-white">Notifications</p>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-2xl mb-2">🔔</p>
                <p className="text-sm text-gray-500">No notifications yet</p>
                <p className="text-xs text-gray-600 mt-1">
                  {isPaid ? "You'll see likes and comments here" : t('courseBuilder.notif.empty', 'You’ll see updates about your courses here')}
                </p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.notification_id}
                  onClick={() => handleClick(n)}
                  className="w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-white/[0.04]"
                  style={!n.read ? { backgroundColor: 'var(--lx-muted)' } : {}}
                >
                  <span className="text-lg flex-shrink-0 mt-0.5">{isAiCourseNotification(n) ? aiNotificationIcon(n) : (NOTIF_ICONS[n.type] || '🔔')}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-200 leading-snug">
                      {isAiCourseNotification(n) ? aiNotificationText(n, t) : (NOTIF_LABELS[n.type] || (() => n.actor_name))(n)}
                    </p>
                    {!isAiCourseNotification(n) && n.post_title && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">"{n.post_title}"</p>
                    )}
                    <p className="text-[10px] text-gray-600 mt-1">{timeAgoShort(n.created_at)}</p>
                  </div>
                  {!n.read && (
                    <span className="w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0 mt-1.5" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { t } = useTranslation();
  const { user, isPaid, isLifetime, shadow, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isElectron = useElectron();
  const location = useLocation();
  const [panelOpen, setPanelOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pathsOpen, setPathsOpen] = useState(false);
  const [interviewOpen, setInterviewOpen] = useState(false);
  const [labsOpen, setLabsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const pathsRef = useRef(null);
  const interviewRef = useRef(null);
  const labsRef = useRef(null);
  const resourcesRef = useRef(null);
  const [hasPin, setHasPin]             = useState(!!localStorage.getItem('lxops_has_pin'));
  const [hasPassword, setHasPassword]   = useState(!!localStorage.getItem('lxops_has_password'));
  const [hasPasskey, setHasPasskey]     = useState(!!localStorage.getItem('lxops_has_passkey'));
  const [showPinModal, setShowPinModal] = useState(false);
  const [showPasskeyModal, setShowPasskeyModal] = useState(false);
  const [showPwModal, setShowPwModal]   = useState(false);
  const [searchOpen, setSearchOpen]     = useState(false);

  const initials = user?.name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); setPathsOpen(false); setInterviewOpen(false); setLabsOpen(false); setResourcesOpen(false); }, [location]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (pathsRef.current && !pathsRef.current.contains(e.target)) setPathsOpen(false);
      if (interviewRef.current && !interviewRef.current.contains(e.target)) setInterviewOpen(false);
      if (labsRef.current && !labsRef.current.contains(e.target)) setLabsOpen(false);
      if (resourcesRef.current && !resourcesRef.current.contains(e.target)) setResourcesOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Cmd+K / Ctrl+K → open search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const navLinkClass = (active) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
      active ? 'text-white bg-indigo-600/20 border border-indigo-500/30' : 'text-gray-400'
    }`;

  return (
    <div className="min-h-screen bg-lx-bg">
      {/* Nav */}
      <header className={`sticky top-0 z-50${isElectron ? ' electron-drag' : ''}`} style={{ borderBottom: '1px solid var(--lx-border)', backgroundColor: 'color-mix(in srgb, var(--lx-bg) 92%, transparent)', backdropFilter: 'blur(16px)' }}>
        <div className="relative w-full flex items-center" style={{ height: isElectron ? '4.25rem' : '3.5rem', paddingTop: isElectron ? '1rem' : undefined, paddingBottom: isElectron ? '0.5rem' : undefined, paddingLeft: isElectron && window.electronEnv?.platform === 'darwin' ? '84px' : undefined }}>

          {/* Logo — anchored to the far left */}
          <Link to={user ? '/dashboard' : '/posts'} className="absolute left-4 flex items-center gap-2 flex-shrink-0 group z-10">
            <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="LearnXops" className="w-7 h-7 object-contain" />
            <span className="hidden 2xl:inline font-bold text-white text-base tracking-tight group-hover:text-indigo-400 transition-colors">
              LearnXops
            </span>
          </Link>

          {/* Nav + right — constrained to content width so they align with page content */}
          <div className="lx-header-row max-w-6xl mx-auto px-4 sm:px-6 w-full flex items-center justify-between">

          {/* Desktop nav */}
          {/* Desktop nav from lg up. It measures ~890px wide with the actions
    beside it, so anything narrower was pushing the page sideways —
    those widths get the hamburger below instead. */}
          <nav className="hidden lg:flex items-center gap-1">

            {/* Plain links */}
            {/* Resources dropdown — Posts, Ebooks, System Designs */}
            <div className="relative" ref={resourcesRef}>
              <button data-tour="nav-resources"
                onClick={() => setResourcesOpen((v) => !v)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  resourcesOpen || location.pathname === '/posts' || location.pathname.startsWith('/system-designs') || location.pathname.startsWith('/case-studies') || location.pathname === '/courses' || location.pathname.startsWith('/courses/') || location.pathname === '/ebooks' || location.pathname.startsWith('/ebooks/')
                    ? 'text-white bg-white/[0.06]'
                    : 'text-gray-400 hover:text-white hover:bg-white/[0.05]'
                }`}
              >
                {t('nav.resources')}
                <svg className={`w-3 h-3 mt-0.5 transition-transform ${resourcesOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {resourcesOpen && (
                <div className="absolute top-full left-0 mt-1.5 w-64 rounded-xl py-1.5 z-50 shadow-2xl shadow-black/50"
                  style={{ background: 'var(--lx-surface)', border: '1px solid var(--lx-border)' }}>
                  {[
                    { to: '/posts',             icon: '📰', label: t('nav.posts'),         sub: t('nav.postsSub'),         active: location.pathname === '/posts' && !location.search.includes('tag=') },
                    { to: '/ebooks',            icon: '📚', label: t('nav.ebooks'),        sub: t('nav.ebooksHubSub', 'Community ebooks & your own'), active: location.pathname === '/ebooks' || location.pathname.startsWith('/ebooks/') },
                    { to: '/system-designs',    icon: '✏️', label: t('nav.systemDesigns'), sub: t('nav.systemDesignsSub'), active: location.pathname.startsWith('/system-designs') },
                    { to: '/case-studies',      icon: '🏗️', label: t('nav.caseStudies', 'Case Studies'), sub: t('nav.caseStudiesSub', 'Real-world postmortems and lessons'), active: location.pathname.startsWith('/case-studies') },
                    { to: '/career-roadmaps',   icon: '🧭', label: t('nav.careerRoadmaps'), sub: t('nav.careerRoadmapsSub'), active: location.pathname.startsWith('/career-roadmaps') },
                    { to: '/courses',           icon: '🎓', label: t('nav.courses'),       sub: t('nav.coursesSub'),       active: location.pathname === '/courses' || location.pathname.startsWith('/courses/') },
                  ].map(({ to, icon, label, sub, active }) => (
                    <Link key={to} to={to}
                      className={`nav-dd-item flex items-center gap-3 px-3 py-2 text-sm rounded-lg mx-1 ${active ? 'text-white bg-white/[0.06]' : 'text-gray-400 hover:text-white'}`}>
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center text-base flex-shrink-0" style={{ background: 'var(--lx-muted)' }}>{icon}</span>
                      <div>
                        <div className="font-medium leading-tight">{label}</div>
                        <div className="text-[10px] text-gray-600 mt-0.5">{sub}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Plain links */}
            {[
              { to: '/projects',        emoji: '🛠️', label: t('nav.projects'), active: location.pathname === '/projects' },
            ].map(({ to, label, active }) => (
              <Link key={to} to={to} data-tour="nav-projects"
                className={`relative px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${active ? 'text-white' : 'text-gray-400 hover:text-white hover:bg-white/[0.05]'}`}>
                {label}
                {active && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-indigo-400"/>}
              </Link>
            ))}

            {/* Labs dropdown */}
            <div className="relative" ref={labsRef}>
              <button data-tour="nav-labs"
                onClick={() => setLabsOpen((v) => !v)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  labsOpen || location.pathname.startsWith('/labs')
                    ? 'text-white bg-white/[0.06]'
                    : 'text-gray-400 hover:text-white hover:bg-white/[0.05]'
                }`}
              >
                {t('nav.labs')}
                <svg className={`w-3 h-3 mt-0.5 transition-transform ${labsOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {labsOpen && (
                <div className="absolute top-full left-0 mt-1.5 w-64 rounded-xl py-1.5 z-50 shadow-2xl shadow-black/50"
                  style={{ background: 'var(--lx-surface)', border: '1px solid var(--lx-border)' }}>
                  {[
                    { to: '/labs',            icon: '🧪', label: t('nav.allLabs'), sub: t('nav.allLabsSub'), active: location.pathname === '/labs' || location.pathname.startsWith('/labs/category') || location.pathname.startsWith('/labs/notes') || (location.pathname.startsWith('/labs/') && location.pathname !== '/labs/automation' && !location.pathname.startsWith('/labs/kubernetes') && !location.pathname.startsWith('/labs/local-cloud') && !location.pathname.startsWith('/labs/ai-agents')) },
                    { to: '/labs/kubernetes', icon: '☸️', label: t('nav.kubernetes'), sub: t('nav.kubernetesSub'), active: location.pathname === '/labs/kubernetes' || location.pathname.startsWith('/labs/kubernetes/') },
                    { to: '/labs/local-cloud', icon: '🌩️', label: t('nav.localCloud'), sub: t('nav.localCloudSub'), active: location.pathname === '/labs/local-cloud' || location.pathname.startsWith('/labs/local-cloud/') },
                    { to: '/labs/ai-agents',  icon: '🤖', label: t('nav.agentLabs'), sub: t('nav.agentLabsSub'), active: location.pathname === '/labs/ai-agents' || location.pathname.startsWith('/labs/ai-agents/') },
                    { to: '/certificates',    icon: '🏅', label: t('certificates.navLabel'), sub: t('certificates.navSub'), active: location.pathname === '/certificates' },
                  ].map(({ to, icon, label, sub, active }) => (
                    <Link key={to} to={to}
                      className={`nav-dd-item flex items-center gap-3 px-3 py-2 text-sm rounded-lg mx-1 ${active ? 'text-white bg-white/[0.06]' : 'text-gray-400 hover:text-white'}`}>
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center text-base flex-shrink-0" style={{ background: 'var(--lx-muted)' }}>{icon}</span>
                      <div>
                        <div className="font-medium leading-tight">{label}</div>
                        <div className="text-[10px] text-gray-600 mt-0.5">{sub}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Learning Paths dropdown */}
            <div className="relative" ref={pathsRef}>
              <button data-tour="nav-paths"
                onClick={() => setPathsOpen((v) => !v)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  pathsOpen || location.pathname.startsWith('/learning-paths')
                    ? 'text-white bg-white/[0.06]'
                    : 'text-gray-400 hover:text-white hover:bg-white/[0.05]'
                }`}
              >
                {t('nav.learningPaths')}
                <svg className={`w-3 h-3 mt-0.5 transition-transform ${pathsOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {pathsOpen && (
                <div className="absolute top-full left-0 mt-1.5 w-56 rounded-xl py-1.5 z-50 shadow-2xl shadow-black/50"
                  style={{ background: 'var(--lx-surface)', border: '1px solid var(--lx-border)' }}>
                  {Object.values(LEARNING_PATHS).map((p) => (
                    <Link key={p.slug} to={`/learning-paths/${p.slug}`}
                      className={`nav-dd-item flex items-center gap-3 px-3 py-2 text-sm rounded-lg mx-1 ${location.pathname === `/learning-paths/${p.slug}` ? 'text-white bg-white/[0.06]' : 'text-gray-400 hover:text-white'}`}>
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center text-base flex-shrink-0" style={{ background: 'var(--lx-muted)' }}>{p.icon}</span>
                      <div>
                        <div className="font-medium leading-tight">{p.labelKey ? t(p.labelKey) : p.label}</div>
                        <div className="text-[10px] text-gray-600 mt-0.5">{t('learningPath.rmDaysCount', { days: p.totalDays })}</div>
                      </div>
                    </Link>
                  ))}
                  <div className="my-1 mx-3" style={{ height: '1px', background: 'var(--lx-border)' }} />
                  <Link to="/learning-paths/roadmap"
                    className={`nav-dd-item flex items-center gap-3 px-3 py-2 text-sm rounded-lg mx-1 ${location.pathname === '/learning-paths/roadmap' ? 'text-indigo-300 bg-indigo-500/10' : 'text-indigo-400 hover:text-indigo-300'}`}>
                    <span className="w-7 h-7 rounded-lg bg-indigo-500/15 flex items-center justify-center text-base flex-shrink-0">✨</span>
                    <div>
                      <div className="font-medium leading-tight">{t('nav.aiRoadmap')}</div>
                      <div className="text-[10px] text-indigo-600 mt-0.5">{t('nav.aiRoadmapSub')}</div>
                    </div>
                  </Link>
                </div>
              )}
            </div>

            {/* Interview & Certs dropdown */}
            <div className="relative" ref={interviewRef}>
              <button data-tour="nav-jobs"
                onClick={() => setInterviewOpen((v) => !v)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  interviewOpen || ['/jobs','/interview','/virtual-interview','/job-tracker','/job-agent','/cert-exam'].includes(location.pathname) || location.pathname.startsWith('/resume-builder') || location.pathname.startsWith('/cover-letter')
                    ? 'text-white bg-white/[0.06]'
                    : 'text-gray-400 hover:text-white hover:bg-white/[0.05]'
                }`}
              >
                {t('nav.jobsInterviews')}
                <svg className={`w-3 h-3 mt-0.5 transition-transform ${interviewOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {interviewOpen && (
                <div className="absolute top-full left-0 mt-1.5 w-60 rounded-xl py-1.5 z-50 shadow-2xl shadow-black/50"
                  style={{ background: 'var(--lx-surface)', border: '1px solid var(--lx-border)' }}>
                  <p className="px-4 pt-1.5 pb-1 text-[10px] font-semibold text-gray-600 uppercase tracking-wider">{t('nav.jobsHeader')}</p>
                  {[
                    { to: '/jobs',              icon: '💼', label: t('nav.jobListings'),        sub: t('nav.jobListingsSub') },
                    { to: '/job-tracker',       icon: '📊', label: t('nav.jobTracker'),  sub: t('nav.jobTrackerSub') },
                    ...(jobAgentVisible() ? [{ to: '/job-agent', icon: '🤖', label: t('nav.jobAgent', 'Job Agent'), sub: t('nav.jobAgentSub', 'Finds, tailors and applies for you') }] : []),
                  ].map(({ to, icon, label, sub }) => (
                    <Link key={to} to={to}
                      className={`nav-dd-item flex items-center gap-3 px-3 py-2 text-sm rounded-lg mx-1 ${location.pathname === to ? 'text-white bg-white/[0.06]' : 'text-gray-400 hover:text-white'}`}>
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center text-base flex-shrink-0" style={{ background: 'var(--lx-muted)' }}>{icon}</span>
                      <div>
                        <div className="font-medium leading-tight">{label}</div>
                        <div className="text-[10px] text-gray-600 mt-0.5">{sub}</div>
                      </div>
                    </Link>
                  ))}
                  <div className="my-1 mx-3" style={{ height: '1px', background: 'var(--lx-border)' }} />
                  <p className="px-4 pt-1 pb-1 text-[10px] font-semibold text-gray-600 uppercase tracking-wider">{t('nav.interviewPrep')}</p>
                  {[
                    { to: '/resume-builder',    icon: '📄', label: t('nav.resumeBuilder'),      sub: t('nav.resumeBuilderSub') },
                    { to: '/cover-letter',      icon: '✉️', label: t('nav.coverLetter'), sub: t('nav.coverLetterSub') },
                    { to: '/interview',         icon: '📚', label: t('nav.qaLibrary'),        sub: t('nav.qaSub') },
                    { to: '/virtual-interview', icon: '🎙️', label: t('nav.mockInterview'),      sub: t('nav.mockInterviewSub') },
                    { to: '/cert-exam',         icon: '🏅', label: t('nav.certExam'),      sub: t('nav.certExamSub') },
                  ].map(({ to, icon, label, sub }) => (
                    <Link key={to} to={to}
                      className={`nav-dd-item flex items-center gap-3 px-3 py-2 text-sm rounded-lg mx-1 ${location.pathname === to ? 'text-white bg-white/[0.06]' : 'text-gray-400 hover:text-white'}`}>
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center text-base flex-shrink-0" style={{ background: 'var(--lx-muted)' }}>{icon}</span>
                      <div>
                        <div className="font-medium leading-tight">{label}</div>
                        <div className="text-[10px] text-gray-600 mt-0.5">{sub}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {isElectron && (
              <Link to="/support"
                className={`relative px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${location.pathname === '/support' ? 'text-white' : 'text-gray-400 hover:text-white hover:bg-white/[0.05]'}`}>
                {t('nav.support')}
                {location.pathname === '/support' && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-indigo-400"/>}
              </Link>
            )}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-1.5 ml-auto">
            {/* Search */}
            <button data-tour="search"
              onClick={() => setSearchOpen(true)}
              title="Search (⌘K)"
              className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] transition-colors border border-transparent hover:border-white/[0.08]"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z"/>
              </svg>
              <span className="text-xs hidden lg:inline">{t('nav.search')}</span>
              <kbd className="hidden lg:inline text-[10px] px-1 py-0.5 rounded border border-gray-700 font-mono text-gray-600">⌘K</kbd>
            </button>

            {/* Notification bell */}
            <NotificationBell signedIn={!!user} isPaid={isPaid} />

            {/* Language switcher */}
            <LanguageSwitcher />

            {/* Theme toggle */}
            <button onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] transition-colors">
              {theme === 'dark'
                ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="5"/><path strokeLinecap="round" d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
                : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
              }
            </button>

            {/*
              * Signed out, this is where sign-in lives; signed in, it is the
              * profile menu. Everything to the left of it — the whole nav —
              * renders either way, because a visitor who cannot see what the
              * product contains has no reason to make an account. The links
              * themselves behave exactly as before: a members-only route still
              * sends them to /login.
              */}
            {user ? (
            <div className="relative">
              <button data-tour="user-menu" onClick={() => setPanelOpen((v) => !v)}
                className={`flex items-center gap-2 pl-1.5 pr-2.5 py-1 rounded-xl text-sm transition-all ${panelOpen ? 'bg-white/[0.08]' : 'hover:bg-white/[0.06]'}`}
                style={{ border: `1px solid ${panelOpen ? 'var(--lx-border)' : 'transparent'}` }}>
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                  {initials}
                </div>
                <div className="hidden sm:flex flex-col items-start leading-none">
                  <span className="text-xs font-medium text-gray-200 max-w-[80px] truncate">{user?.name?.split(' ')[0]}</span>
                  {(isLifetime || isPaid) && (
                    <span className={`text-[9px] font-semibold mt-0.5 ${isLifetime ? 'text-amber-400' : 'text-indigo-400'}`}>
                      {isLifetime ? '♾ Lifetime' : '✦ Pro'}
                    </span>
                  )}
                </div>
                <svg className={`w-3 h-3 text-gray-600 transition-transform ${panelOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {panelOpen && (
                <ProfilePanel
                  onClose={() => setPanelOpen(false)}
                  hasPin={hasPin} setHasPin={setHasPin} setShowPinModal={setShowPinModal}
                  hasPassword={hasPassword} setHasPassword={setHasPassword} setShowPwModal={setShowPwModal}
                  hasPasskey={hasPasskey} setShowPasskeyModal={setShowPasskeyModal}
                />
              )}
            </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <Link to="/login"
                  className="text-sm text-gray-400 hover:text-white transition-colors px-2.5 py-1.5 rounded-lg">
                  {t('nav.signIn')}
                </Link>
                <Link to="/signup"
                  className="text-sm font-semibold text-white px-3 py-1.5 rounded-lg whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg,#7c3aed,#8b5cf6)' }}>
                  {t('nav.getStarted')}
                </Link>
              </div>
            )}

            {/* Hamburger — mobile only */}
            <button
              data-tour="menu-button"
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="lg:hidden w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white transition-colors"
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--lx-surface)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
          </div>{/* end max-w-6xl nav+right wrapper */}
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden px-4 pb-4 pt-2 animate-fade-in" style={{ borderTop: '1px solid var(--lx-border)', backgroundColor: 'color-mix(in srgb, var(--lx-bg) 95%, transparent)' }}>
            <nav className="flex flex-col gap-1">
              <p className="px-4 pt-2 pb-1 text-[10px] text-gray-600 uppercase tracking-wider font-semibold">{t('nav.resources')}</p>
              <Link to="/posts" className={navLinkClass(location.pathname === '/posts' && !location.search.includes('tag='))}>
                <span>📰</span> {t('nav.posts')}
              </Link>
              <Link to="/ebooks" className={navLinkClass(location.pathname === '/ebooks' || location.pathname.startsWith('/ebooks/'))}>
                <span>📚</span> {t('nav.ebooks')}
              </Link>
              <Link to="/system-designs" className={navLinkClass(location.pathname.startsWith('/system-designs'))}>
                <span>✏️</span> {t('nav.systemDesigns')}
              </Link>
              <Link to="/case-studies" className={navLinkClass(location.pathname.startsWith('/case-studies'))}>
                <span>🏗️</span> {t('nav.caseStudies', 'Case Studies')}
              </Link>
              <Link to="/career-roadmaps" className={navLinkClass(location.pathname.startsWith('/career-roadmaps'))}>
                <span>🧭</span> {t('nav.careerRoadmaps')}
              </Link>
              <Link to="/courses" className={navLinkClass(location.pathname === '/courses' || location.pathname.startsWith('/courses/'))}>
                <span>🎓</span> {t('nav.courses')}
              </Link>
              <Link to="/projects" className={navLinkClass(location.pathname === '/projects')}>
                <span>🛠️</span> {t('nav.projects')}
              </Link>
              <Link to="/labs" className={navLinkClass(location.pathname === '/labs' || location.pathname.startsWith('/labs/category') || location.pathname.startsWith('/labs/notes') || (location.pathname.startsWith('/labs/') && location.pathname !== '/labs/automation' && !location.pathname.startsWith('/labs/kubernetes') && !location.pathname.startsWith('/labs/local-cloud') && !location.pathname.startsWith('/labs/ai-agents')))}>
                <span>🧪</span> {t('nav.allLabs')}
              </Link>
              <Link to="/labs/kubernetes" className={navLinkClass(location.pathname === '/labs/kubernetes' || location.pathname.startsWith('/labs/kubernetes/'))}>
                <span>☸️</span> {t('nav.kubernetes')}
              </Link>
              <Link to="/labs/local-cloud" className={navLinkClass(location.pathname === '/labs/local-cloud' || location.pathname.startsWith('/labs/local-cloud/'))}>
                <span>🌩️</span> {t('nav.localCloud')}
              </Link>
              <Link to="/labs/ai-agents" className={navLinkClass(location.pathname === '/labs/ai-agents' || location.pathname.startsWith('/labs/ai-agents/'))}>
                <span>🤖</span> {t('nav.agentLabs')}
              </Link>
              <Link to="/certificates" className={navLinkClass(location.pathname === '/certificates')}>
                <span>🏅</span> {t('certificates.navLabel')}
              </Link>
              <p className="px-4 pt-2 pb-1 text-[10px] text-gray-600 uppercase tracking-wider font-semibold">{t('nav.learningPaths')}</p>
              {Object.values(LEARNING_PATHS).map((p) => (
                <Link key={p.slug} to={`/learning-paths/${p.slug}`} className={navLinkClass(location.pathname === `/learning-paths/${p.slug}`)}>
                  <span>{p.icon}</span> {p.label}
                </Link>
              ))}
              <Link to="/learning-paths/roadmap" className={navLinkClass(location.pathname === '/learning-paths/roadmap')}
                style={location.pathname === '/learning-paths/roadmap' ? {} : { color: '#818cf8' }}>
                <span>✨</span> {t('nav.myAiRoadmap')}
              </Link>
              <p className="px-4 pt-2 pb-1 text-[10px] text-gray-600 uppercase tracking-wider font-semibold">{t('nav.jobsInterviews')}</p>
              <Link to="/jobs" className={navLinkClass(location.pathname === '/jobs')}>
                <span>💼</span> {t('nav.jobListings')}
              </Link>
              <Link to="/job-tracker" className={navLinkClass(location.pathname === '/job-tracker')}>
                <span>📊</span> {t('nav.jobTracker')}
              </Link>
              {jobAgentVisible() && (
                <Link to="/job-agent" className={navLinkClass(location.pathname === '/job-agent')}>
                  <span>🤖</span> {t('nav.jobAgent', 'Job Agent')}
                </Link>
              )}
              <Link to="/resume-builder" className={navLinkClass(location.pathname.startsWith('/resume-builder'))}>
                <span>📄</span> {t('nav.resumeBuilder')}
              </Link>
              <Link to="/cover-letter" className={navLinkClass(location.pathname.startsWith('/cover-letter'))}>
                <span>✉️</span> {t('nav.coverLetter')}
              </Link>
              <Link to="/interview" className={navLinkClass(location.pathname === '/interview')}>
                <span>📚</span> {t('nav.qa')}
              </Link>
              <Link to="/virtual-interview" className={navLinkClass(location.pathname === '/virtual-interview')}>
                <span>🎙️</span> {t('nav.mockInterview')}
              </Link>
              <Link to="/cert-exam" className={navLinkClass(location.pathname === '/cert-exam')}>
                <span>🏅</span> {t('nav.certExam')}
              </Link>
              {isElectron && (
                <Link to="/support" className={navLinkClass(location.pathname === '/support')}>
                  <span>🛟</span> {t('nav.support')}
                </Link>
              )}

              {/* sidebar sections — shown in mobile menu */}
              {SIDEBAR_SECTIONS.map((section) => (
                <React.Fragment key={section.id}>
                  <p className="px-4 pt-2 pb-1 text-[10px] text-gray-600 uppercase tracking-wider font-semibold">{section.label}</p>
                  {section.items.filter((item) => !item.visible || item.visible()).map((item) => {
                    const locked = item.paidOnly && !isPaid && !isLifetime;
                    return (
                      <Link
                        key={item.id}
                        to={item.to}
                        className={navLinkClass(item.isActive(location))}
                      >
                        <span>{item.emoji}</span>
                        {item.label}
                        {item.paidOnly && (
                          <span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                            locked ? 'bg-gray-600/30 text-gray-500' : 'bg-indigo-500/20 text-indigo-400'
                          }`}>
                            {locked ? 'PRO' : 'PRO'}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </React.Fragment>
              ))}
            </nav>
          </div>
        )}
      </header>

      {user && <WelcomeTour />}

      {/* body: sidebar + content */}
      <div className="flex">
        <LeftSidebar />
        <div className="flex-1 min-w-0 flex flex-col">
          <main className="flex-1">{children}</main>
          <Footer />
        </div>
      </div>

      {showPwModal && (
        <PasswordSetupModal
          existingPassword={hasPassword}
          onClose={() => setShowPwModal(false)}
          onPasswordChanged={(state) => {
            setHasPassword(state);
            localStorage.setItem('lxops_has_password', state ? 'true' : '');
          }}
        />
      )}

      {shadow && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 60, padding: '8px 16px',
          background: 'linear-gradient(90deg,#b45309,#d97706)', color: '#fff',
          fontSize: 13, fontWeight: 600, display: 'flex', gap: 12,
          alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap',
        }}>
          <span>
            👁 Admin view — you are seeing LearnXops as{' '}
            <strong>{user?.email}</strong>. Started by {shadow.actor}.
          </span>
          <button onClick={logout}
            style={{
              background: 'rgba(0,0,0,.25)', border: '1px solid rgba(255,255,255,.35)',
              color: '#fff', borderRadius: 8, padding: '3px 10px',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}>
            End session
          </button>
        </div>
      )}

      {showPasskeyModal && (
        <PasskeySetupModal
          onClose={() => setShowPasskeyModal(false)}
          onPasskeysChanged={(state) => {
            setHasPasskey(state);
            if (state) localStorage.setItem('lxops_has_passkey', 'true');
            else localStorage.removeItem('lxops_has_passkey');
          }}
        />
      )}

      {showPinModal && (
        <PinSetupModal
          existingPin={hasPin}
          onClose={() => setShowPinModal(false)}
          onPinChanged={(state) => setHasPin(state)}
        />
      )}

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
